/**
 * RandomNamePicker.tsx
 * Animated spinning wheel that randomly selects a student from the roster.
 */

import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, RefreshCw } from 'lucide-react';
import { usePresenterStore } from '@store/presenterStore';
import { useClassStore } from '@store/classStore';

export default function RandomNamePicker(): React.ReactElement {
  const { setNamePickerOpen } = usePresenterStore();
  const roster = useClassStore((s) => s.roster);
  const [spinning, setSpinning] = useState(false);
  const [picked, setPicked] = useState<string | null>(null);
  const [pickedEmoji, setPickedEmoji] = useState('');

  const spin = useCallback(() => {
    if (roster.length === 0 || spinning) return;
    setPicked(null);
    setSpinning(true);

    // Animate through names rapidly, then settle
    let cycles = 0;
    const maxCycles = 18 + Math.floor(Math.random() * 12);
    const interval = setInterval(() => {
      const random = roster[Math.floor(Math.random() * roster.length)];
      setPicked(random.name);
      setPickedEmoji(random.avatar);
      cycles++;
      if (cycles >= maxCycles) {
        clearInterval(interval);
        setSpinning(false);
      }
    }, 80 + cycles * 8);
  }, [roster, spinning]);

  return (
    <motion.div
      className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[9030] w-72 glass rounded-3xl overflow-hidden shadow-glass pointer-events-auto"
      initial={{ scale: 0.8, opacity: 0, y: 20 }}
      animate={{ scale: 1, opacity: 1, y: 0 }}
      exit={{ scale: 0.8, opacity: 0, y: 20 }}
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-surface-border">
        <span className="text-sm font-bold text-slate-900">🎲 Random Picker</span>
        <button onClick={() => setNamePickerOpen(false)}
          className="p-1.5 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-surface-elevated transition-colors">
          <X size={14} />
        </button>
      </div>

      <div className="p-6 flex flex-col items-center gap-4">
        {/* Display picked name */}
        <div className="w-full h-24 flex flex-col items-center justify-center rounded-2xl bg-surface-elevated border border-surface-border">
          <AnimatePresence mode="wait">
            {picked ? (
              <motion.div key={picked} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                className="flex flex-col items-center gap-1">
                <span className="text-3xl">{pickedEmoji}</span>
                <span className={`text-lg font-bold text-slate-900 ${spinning ? 'blur-[1px]' : ''} transition-all`}>
                  {picked}
                </span>
              </motion.div>
            ) : (
              <motion.p key="placeholder" className="text-sm text-slate-500">
                Click Spin to pick!
              </motion.p>
            )}
          </AnimatePresence>
        </div>

        {/* Spin button */}
        <motion.button
          onClick={spin}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          disabled={roster.length === 0}
          className="w-full py-3 rounded-2xl bg-gradient-to-r from-brand-500 to-accent-500 text-white font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-50"
        >
          <RefreshCw size={16} className={spinning ? 'animate-spin' : ''} />
          {spinning ? 'Picking…' : 'Spin!'}
        </motion.button>

        {roster.length === 0 && (
          <p className="text-xs text-slate-500 text-center">Add students to your class first</p>
        )}
      </div>
    </motion.div>
  );
}
